/**
 * Static Odoo operation schema cheat-sheet.
 *
 * 這是「一份手動整理的精簡 schema」，用於 Cheat-sheet 面板與 Prompt 欄位選擇器，
 * 讓使用者在不開 Odoo 後台的情況下，能直接看到每個 operationType 接受哪些 master / line 欄位、
 * 哪些是必填、哪些是 FK 參照，以及一份範例 JSON。
 *
 * 資料來源：Services/Odoo/OdooCommandService.cs（134-451 行）+ 流程節點的高階 OdooCommandPayload schema。
 *
 * 注意：這份是「白盒」可預期欄位；如果你需要 Odoo 原生欄位完整清單，請改用：
 *   - mapping 表的 OdooFieldAutoComplete（連 fields_get cache）
 *   - 或在 Prompt picker 切到「完整 Schema (fields_get)」分組
 */

const lookupCommon = (modelLabel) => ({
  label: `查詢 ${modelLabel}`,
  master: [
    { path: 'query', label: '模糊查詢字串', type: 'string',
      tip: 'res.partner: name ilike；product.product: default_code | name ilike；其它: display_name ilike' },
    { path: 'domain', label: '進階 Odoo domain（陣列陣列）', type: 'array',
      tip: '例如 [["customer_rank",">",0]]，會與 query 結果合併' },
    { path: 'resultVariable', label: '結果寫入流程變數', type: 'string' }
  ],
  lines: [],
  fk: [],
  example: {
    version: '1.0',
    operations: [
      { action: 'lookupPartner', query: 'ACME', domain: [['customer_rank', '>', 0]] }
    ]
  },
  tips: [
    'Lookup 操作只讀，不會建檔。',
    '結果會放進 Extra.records（最多 50 筆）；若要寫進 PV，用 resultVariable 名稱。'
  ]
});

const odooOperationSchemas = {
  // ───────────── Sales ─────────────
  createSO: {
    label: '建立銷售單 (SO)',
    odooModel: 'sale.order',
    odooMethod: 'create + action_confirm',
    master: [
      { path: 'partner', label: '客戶', required: true, fk: 'res.partner',
        tip: 'partner.id 優先；否則 partner.email = ilike，再不然 partner.name = ilike；找不到自動建檔（customer_rank=1）' },
      { path: 'partner.id', label: '客戶 Id', fk: 'res.partner', type: 'integer' },
      { path: 'partner.name', label: '客戶名稱', type: 'string' },
      { path: 'partner.email', label: '客戶 Email', type: 'string' },
      { path: 'partner.phone', label: '客戶電話', type: 'string' },
      { path: 'terms.pricelistId', label: '價目表', fk: 'product.pricelist', type: 'integer' },
      { path: 'terms.paymentTermId', label: '付款條件', fk: 'account.payment.term', type: 'integer' },
      { path: 'terms.commitmentDate', label: '交貨日 (commitment_date)', type: 'date' },
      { path: 'terms.currencyId', label: '幣別', fk: 'res.currency', type: 'integer' },
      { path: 'externalRef', label: '客戶單號 (client_order_ref)', type: 'string' },
      { path: 'company.id', label: '公司 Id', fk: 'res.company', type: 'integer' },
      { path: 'notes', label: '備註 (note)', type: 'string' }
    ],
    lines: [
      { path: 'lines[i].product', label: '產品', required: true, fk: 'product.product',
        tip: 'product.id / product.code (default_code) / product.barcode / product.name 任一即可解析' },
      { path: 'lines[i].product.id', label: '產品 Id', fk: 'product.product', type: 'integer' },
      { path: 'lines[i].product.code', label: '產品 SKU (default_code)', type: 'string' },
      { path: 'lines[i].product.barcode', label: '產品 barcode', type: 'string' },
      { path: 'lines[i].product.name', label: '產品名稱', type: 'string' },
      { path: 'lines[i].qty', label: '數量', required: true, type: 'number' },
      { path: 'lines[i].price', label: '單價 (price_unit)', type: 'number' },
      { path: 'lines[i].discount', label: '折扣 %', type: 'number' },
      { path: 'lines[i].description', label: '行說明 → name', type: 'string' }
    ],
    fk: ['res.partner', 'product.product', 'product.pricelist', 'account.payment.term', 'res.currency', 'res.company'],
    example: {
      version: '1.0',
      operations: [{
        action: 'createSO',
        externalRef: 'wf-123-node-abc',
        partner: { name: 'ACME Limited', email: 'po@acme.com' },
        terms: { pricelistId: 1, commitmentDate: '2026-06-01' },
        lines: [
          { product: { code: 'SKU-001' }, qty: 10, price: 200 },
          { product: { code: 'SKU-002' }, qty: 5, price: 350 }
        ]
      }]
    },
    tips: [
      '預設會自動 action_confirm（state=sale）。',
      '若要先看草稿，請改用 createQuotation。',
      '建立後若要開發票，下一個節點走 createInvoice，並把 source.id 設為此 SO 的 odooResultVariable.id。'
    ]
  },

  createQuotation: {
    label: '建立報價單 (草稿 SO)',
    odooModel: 'sale.order',
    odooMethod: 'create',
    master: [
      { path: 'partner', label: '客戶', required: true, fk: 'res.partner',
        tip: '同 createSO；id / email / name 任一' },
      { path: 'terms.pricelistId', label: '價目表', fk: 'product.pricelist', type: 'integer' },
      { path: 'terms.paymentTermId', label: '付款條件', fk: 'account.payment.term', type: 'integer' },
      { path: 'terms.commitmentDate', label: '交貨日', type: 'date' },
      { path: 'externalRef', label: '客戶單號', type: 'string' },
      { path: 'company.id', label: '公司 Id', fk: 'res.company', type: 'integer' },
      { path: 'notes', label: '備註', type: 'string' }
    ],
    lines: [
      { path: 'lines[i].product', label: '產品', required: true, fk: 'product.product' },
      { path: 'lines[i].qty', label: '數量', required: true, type: 'number' },
      { path: 'lines[i].price', label: '單價', type: 'number' },
      { path: 'lines[i].discount', label: '折扣 %', type: 'number' },
      { path: 'lines[i].description', label: '行說明', type: 'string' }
    ],
    fk: ['res.partner', 'product.product', 'product.pricelist', 'account.payment.term'],
    example: {
      version: '1.0',
      operations: [{
        action: 'createQuotation',
        partner: { id: 42 },
        lines: [{ product: { code: 'SKU-001' }, qty: 1 }]
      }]
    },
    tips: [
      '與 createSO 同 schema，差別在不會自動 confirm，state=draft。',
      '之後可呼叫 confirmSO 完成轉單。'
    ]
  },

  confirmSO: {
    label: '確認銷售單',
    odooModel: 'sale.order',
    odooMethod: 'action_confirm',
    master: [
      { path: 'source.id', label: 'SO 的 Id', required: true, fk: 'sale.order', type: 'integer' }
    ],
    lines: [],
    fk: ['sale.order'],
    example: { version: '1.0', operations: [{ action: 'confirmSO', source: { id: 12345 } }] },
    tips: ['只需要 source.id；上一步可從 odooResultVariable 取得。']
  },

  // ───────────── Purchase ─────────────
  createPO: {
    label: '建立採購單 (PO)',
    odooModel: 'purchase.order',
    odooMethod: 'create (+ button_confirm if metadata.autoConfirm)',
    master: [
      { path: 'partner', label: '供應商', required: true, fk: 'res.partner',
        tip: 'id / email / name 任一；找不到自動建檔（supplier_rank=1）' },
      { path: 'terms.paymentTermId', label: '付款條件', fk: 'account.payment.term', type: 'integer' },
      { path: 'terms.commitmentDate', label: '預計到貨日 (date_planned)', type: 'date' },
      { path: 'externalRef', label: '供應商引用 (partner_ref)', type: 'string' },
      { path: 'company.id', label: '公司 Id', fk: 'res.company', type: 'integer' },
      { path: 'notes', label: '備註 → notes 欄位', type: 'string' },
      { path: 'metadata.autoConfirm', label: '自動 confirm（節點層 autoConfirm 也會注入）', type: 'boolean' }
    ],
    lines: [
      { path: 'lines[i].product', label: '產品', required: true, fk: 'product.product' },
      { path: 'lines[i].qty', label: '數量 (product_qty)', required: true, type: 'number' },
      { path: 'lines[i].price', label: '單價 (price_unit)', type: 'number' },
      { path: 'lines[i].description', label: '行說明 → name', type: 'string' }
    ],
    fk: ['res.partner', 'product.product', 'account.payment.term', 'res.company'],
    example: {
      version: '1.0',
      operations: [{
        action: 'createPO',
        partner: { name: 'Foxconn' },
        terms: { commitmentDate: '2026-07-15' },
        lines: [{ product: { code: 'PART-001' }, qty: 100, price: 50 }],
        metadata: { autoConfirm: true }
      }]
    },
    tips: [
      '節點層 autoConfirm 開關會被注入到 metadata.autoConfirm；預設 true。',
      'PO 沒有 discount 欄位（不同於 SO）。'
    ]
  },

  // ───────────── Invoice (account.move) ─────────────
  createInvoice: {
    label: '建立發票 (INV)',
    odooModel: 'account.move',
    odooMethod: '_create_invoices(from SO) | create',
    master: [
      { path: 'source.id', label: '來源 SO Id（路徑 A：自動帶銷售單行）', fk: 'sale.order', type: 'integer',
        tip: '若有 source.id 走 sale.order._create_invoices；否則走自建（path B）' },
      { path: 'partner', label: '客戶（path B 必填）', fk: 'res.partner' },
      { path: 'metadata.moveType', label: '發票類型', type: 'string',
        tip: 'out_invoice (應收) | in_invoice (應付) | out_refund | in_refund；預設 out_invoice' },
      { path: 'metadata.autoPost', label: '自動 action_post', type: 'boolean' },
      { path: 'externalRef', label: '客戶單號 → ref', type: 'string' },
      { path: 'company.id', label: '公司 Id', fk: 'res.company', type: 'integer' }
    ],
    lines: [
      { path: 'lines[i].product', label: '產品', fk: 'product.product',
        tip: 'path B 才會用到，path A 由 SO 帶' },
      { path: 'lines[i].qty', label: '數量', type: 'number' },
      { path: 'lines[i].price', label: '單價 (price_unit)', type: 'number' },
      { path: 'lines[i].description', label: '行說明 → name', type: 'string' }
    ],
    fk: ['sale.order', 'res.partner', 'product.product', 'res.company'],
    example: {
      version: '1.0',
      operations: [{
        action: 'createInvoice',
        source: { id: 12345 },
        metadata: { autoPost: false }
      }]
    },
    tips: [
      'Path A：source.id 指向 sale.order；Path B：直接給 partner + lines 自建發票。',
      '節點層 autoPost 開關會注入到 metadata.autoPost。'
    ]
  },

  postInvoice: {
    label: '過帳發票',
    odooModel: 'account.move',
    odooMethod: 'action_post',
    master: [
      { path: 'source.id', label: '發票 Id', required: true, fk: 'account.move', type: 'integer' }
    ],
    lines: [],
    fk: ['account.move'],
    example: { version: '1.0', operations: [{ action: 'postInvoice', source: { id: 9001 } }] },
    tips: ['只需要 source.id；通常接在 createInvoice 之後（autoPost=false 時）。']
  },

  createCreditNote: {
    label: '建立信用備忘錄（反開發票）',
    odooModel: 'account.move',
    odooMethod: '_reverse_moves | account.move.reversal',
    master: [
      { path: 'source.id', label: '原發票 Id', required: true, fk: 'account.move', type: 'integer' }
    ],
    lines: [],
    fk: ['account.move'],
    example: { version: '1.0', operations: [{ action: 'createCreditNote', source: { id: 9001 } }] },
    tips: ['會自動 fallback 到 account.move.reversal wizard，相容多版本 Odoo。']
  },

  // ───────────── Stock pickings ─────────────
  createDeliveryNote: {
    label: '建立出貨單 (DN)',
    odooModel: 'stock.picking',
    odooMethod: 'create + action_confirm (+ assign + button_validate if autoValidate)',
    master: [
      { path: 'partner', label: '客戶', required: true, fk: 'res.partner' },
      { path: 'externalRef', label: '來源單號 → origin', type: 'string' },
      { path: 'company.id', label: '公司 Id', fk: 'res.company', type: 'integer' },
      { path: 'metadata.autoValidate', label: '自動 validate（庫存可用才會成功）', type: 'boolean' }
    ],
    lines: [
      { path: 'lines[i].product', label: '產品', required: true, fk: 'product.product' },
      { path: 'lines[i].qty', label: '出貨數量 (product_uom_qty)', required: true, type: 'number' }
    ],
    fk: ['res.partner', 'product.product', 'stock.picking.type', 'res.company'],
    example: {
      version: '1.0',
      operations: [{
        action: 'createDeliveryNote',
        partner: { id: 42 },
        externalRef: 'SO-2026-0001',
        lines: [{ product: { code: 'SKU-001' }, qty: 10 }]
      }]
    },
    tips: [
      '會找 picking_type code=outgoing；若有 defaultCompanyId 會限定該公司。',
      'autoValidate=true 時，庫存若不足會中斷；建議保持 false 由人工確認。'
    ]
  },

  createGoodsReceive: {
    label: '建立收貨單 (GR)',
    odooModel: 'stock.picking',
    odooMethod: 'create + action_confirm (+ button_validate if autoValidate)',
    master: [
      { path: 'partner', label: '供應商', required: true, fk: 'res.partner' },
      { path: 'externalRef', label: '來源單號 → origin', type: 'string' },
      { path: 'company.id', label: '公司 Id', fk: 'res.company', type: 'integer' },
      { path: 'metadata.autoValidate', label: '自動 validate', type: 'boolean' }
    ],
    lines: [
      { path: 'lines[i].product', label: '產品', required: true, fk: 'product.product' },
      { path: 'lines[i].qty', label: '收貨數量', required: true, type: 'number' }
    ],
    fk: ['res.partner', 'product.product', 'stock.picking.type', 'res.company'],
    example: {
      version: '1.0',
      operations: [{
        action: 'createGoodsReceive',
        partner: { name: 'Foxconn' },
        externalRef: 'PO-2026-0001',
        lines: [{ product: { code: 'PART-001' }, qty: 100 }]
      }]
    },
    tips: ['picking_type code=incoming；其餘行為與 DN 對稱。']
  },

  createReturn: {
    label: '建立退貨單',
    odooModel: 'stock.picking',
    odooMethod: 'stock.return.picking wizard → create_returns',
    master: [
      { path: 'source.id', label: '原 picking Id（來源 DN/GR）', required: true, fk: 'stock.picking', type: 'integer' }
    ],
    lines: [],
    fk: ['stock.picking'],
    example: { version: '1.0', operations: [{ action: 'createReturn', source: { id: 5566 } }] },
    tips: ['後端會走 wizard：開單 → _onchange_picking_id → create_returns。']
  },

  // ───────────── Payment ─────────────
  registerPayment: {
    label: '登記收款',
    odooModel: 'account.payment',
    odooMethod: 'account.payment.register wizard',
    master: [
      { path: 'source.id', label: '發票 Id（account.move）', required: true, fk: 'account.move', type: 'integer' },
      { path: 'metadata.amount', label: '金額', type: 'number' },
      { path: 'metadata.paymentDate', label: '付款日', type: 'date' },
      { path: 'metadata.journalId', label: '日記帳 Id', fk: 'account.journal', type: 'integer' }
    ],
    lines: [],
    fk: ['account.move', 'account.journal'],
    example: {
      version: '1.0',
      operations: [{
        action: 'registerPayment',
        source: { id: 9001 },
        metadata: { amount: 1000, paymentDate: '2026-05-04', journalId: 7 }
      }]
    },
    tips: ['若不指定 amount，Odoo 會以發票應收金額為預設。']
  },

  // ───────────── Lookups ─────────────
  lookupPartner: { ...lookupCommon('Partner / Customer / Vendor'), odooModel: 'res.partner', odooMethod: 'search_read' },
  lookupProduct: { ...lookupCommon('Product'), odooModel: 'product.product', odooMethod: 'search_read' },
  lookupStock:   { ...lookupCommon('Stock Quantity'), odooModel: 'stock.quant', odooMethod: 'search_read' }
};

export default odooOperationSchemas;
